import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Endpoints } from '../../../config/endpoints.config';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { DiskService } from '@omni/services/disk/disk.service';
import { DeviceService } from '@omni/services/device/device.service';
import { AuthenticationService } from '@omni/services/authentication.service';
import { FeatureActionsMap } from '@omni/classes/authentication/user.class';
import { AuthenticationDataService } from '@omni/data-services/authentication/authentication.service';
import { NotificationService, ToastStyle } from '@omni/services/notification/notification.service';
import {
  RxApiPeriodDefinition,
  RxPeriodsApiResponse,
  RxTrendsApiResponse,
  RxTrendsApiRow,
} from '@omni/components/adoption-ladder-journey/rx-trends/rx-trends.api.types';
import { mapRxApiRowsToProductSeriesByHcp } from '@omni/components/adoption-ladder-journey/rx-trends/rx-trends.adapter';
import { RxStoredProductSeries } from '@omni/components/adoption-ladder-journey/rx-trends/rx-trends.types';

/**
 * Rx trend series: fetch from the edge-analytics API, save to the local
 * database, and read back by contact.
 *
 * The journey tab never sees the payload — it reads a document out of
 * PouchDB. That split is the point: the component is unaffected by anything
 * that happens above `saveSeriesToLocalDatabase()`.
 *
 * Backed by the two endpoints specified in **OMNI-61589**, whose client sync
 * rules this implements:
 *
 * 1. `/rx/periods` first — keep its `refreshDate` and period list.
 * 2. `/rx/trends` reports `hasNewData` **independently**, so both are checked.
 * 3. `refreshDate` must match across the two responses; a mismatch means the
 *    warehouse is mid-load and the WK/M indexes do not line up with the
 *    values, so the ingest is abandoned rather than rendered.
 * 4. Page until `hasMore` is false, then store `refreshDate` and send it as
 *    `lastRefreshDate` on the next run.
 * 5. A new snapshot **replaces** the local copy — never a delta merge. Every
 *    refresh shifts what WK1/M1 mean, so rows from two snapshots cannot be
 *    mixed. See `removeDocumentsOutsideSnapshot`.
 */

/** One HCP's document — every product's series for that HCP. */
export interface RxTrendSeriesDocument {
  _id: string;
  _rev?: string;
  /** The HCP's MDM id, as delivered in the payload's `hcpId`. */
  hcpMdmId: string;
  /** Source extract date, `YYYY-MM-DD`, or null if unavailable. */
  dataAsOfDate: string | null;
  /** Numbers and anchors only — labels are derived at render. */
  series: RxStoredProductSeries[];
}

/**
 * Shared window definitions every `RxTrendSeriesDocument` is positioned
 * against. One document, not one per HCP — see `DB_KEY_PREFIXES.RX_TREND_PERIODS`.
 * Kept mainly so a later ingest can compare `refreshDate` against what is
 * already on disk and skip re-fetching everything when nothing changed.
 */
interface RxTrendPeriodsDocument {
  _id: string;
  _rev?: string;
  refreshDate: string;
  periods: RxApiPeriodDefinition[];
  /**
   * Shape/provenance marker for the stored snapshot — see
   * `SNAPSHOT_VERSION`. Absent on anything written before this field existed.
   */
  snapshotVersion?: number;
  /**
   * The server's `syncDate` from the last completed run, echoed back as
   * `lastSyncDate` so it can answer "which HCPs were mapped to my positions
   * since then" — the question `refreshDate` cannot answer, since membership
   * changes without the warehouse moving.
   *
   * Server-issued rather than read off this device's clock, so the window is
   * measured against the same clock that timestamps the position table.
   */
  lastSyncDate?: number;
}

/**
 * Bumped whenever a stored snapshot must not be trusted any more.
 *
 * The stamp is what makes `lastRefreshDate` work, and a stale one is silent
 * and self-perpetuating: the device claims a date it does not really hold, the
 * server correctly answers "nothing newer", and the rows are never fetched.
 *
 * Version 2 retires the bundled JSON fixtures, which carried the *same*
 * `refreshDate` as the QA1 snapshot (`2026-07-30`) — so a device that ever ran
 * on them would otherwise sit on fake series for ever. Documents without this
 * field are pre-v2 and re-ingested once.
 *
 * Bump this on any future change to the stored document shape.
 */
const SNAPSHOT_VERSION = 2;

/** Prefix on every log line from this service, so the ingest is greppable in a device log. */
const LOG = '[RxTrends]';

/**
 * Flattens an error into one string.
 *
 * The Xcode device console renders a logged object as `[object Object]`, and
 * `JSON.stringify` on an Error yields `{}` because its properties are not
 * enumerable — so the fields worth reading are pulled out by hand.
 */
function describeError(error: any): string {
  if (error === null || error === undefined) return 'unknown error';
  if (typeof error === 'string') return error;

  const parts: string[] = [];
  if (error.status !== undefined) parts.push(`status ${error.status}`);
  if (error.statusText) parts.push(error.statusText);
  if (error.message) parts.push(error.message);
  if (parts.length) return parts.join(' | ');

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

const PERIODS_DOCUMENT_ID = DB_KEY_PREFIXES.RX_TREND_PERIODS;

/**
 * How many HCPs are converted and written per batch.
 *
 * Matches `DiskService.bulkInChunks`' own default. It bounds two things at
 * once: the number of documents in a single PouchDB transaction, and how many
 * converted series are held in memory before being flushed.
 */
const HCPS_PER_WRITE_BATCH = 200;

/**
 * Rows requested per page. The endpoint defaults to 500 and **caps at 5,000**
 * (OMNI-61589), so this is the maximum the contract allows.
 *
 * 10,000 was tried and caused iPads to crash mid-sync — the server doesn't
 * clamp or reject the over-cap request (it echoed 10,000 back and honoured it
 * in full), so nothing on the wire stopped it, but a page that size was too
 * much for the device to hold/write. 5,000 is both the documented contract
 * cap and the value that didn't crash.
 *
 * Chosen over fetching several pages concurrently, which buys less than it
 * looks like it should: the network parallelises but the PouchDB writes after
 * each page cannot (they must stay ordered for the carry-forward in
 * `splitTrailingHcp`), so the writes become the floor. Fewer, larger requests
 * cut the round trips without any of the costs — no extra pressure on the
 * app-wide HTTP semaphore (limit 8, and unbounded concurrency through it is
 * what caused OMNI-58047), and no concurrent scans against fact tables the
 * ticket describes as heaps with no index.
 *
 * Against the ~28.7k-row QA1 test position that is 6 requests rather than 29.
 * Peak memory is one page — ~5k rows, comparable to holding 4 concurrent
 * pages of 1,000, so it is not a worse trade.
 */
const ROWS_PER_PAGE = 5000;

/** Longer than the 3s default: it asks the rep to do something. */
const KEEP_APP_OPEN_TOAST_MS = 5000;

/** Lets the sync's own success/failure toast (3s default) finish first. */
const WAIT_OUT_SYNC_TOAST_MS = 3500;

@Injectable({ providedIn: 'root' })
export class RxTrendsDataService {
  /**
   * Guards against several journey-tab opens racing the same one-off ingest.
   * Delete with the sample path — the real sync is driven by the sync lane,
   * not by a component read.
   */
  private ingestionPromise?: Promise<boolean>;

  /**
   * True while the territory's pages are being fetched and written.
   *
   * A territory is ~29 paged requests, and a contact's document only exists once
   * the page carrying it has landed — so an HCP opened mid-ingest has no data
   * yet rather than no prescriptions. Exposed so the journey tab can say which
   * of the two it is instead of rendering nothing.
   */
  private readonly ingestInProgress$ = new BehaviorSubject<boolean>(false);

  /**
   * How many fetch-and-write runs are on the flag — territory paging and
   * per-HCP prefetches alike.
   *
   * Counted rather than set, because whoever raised it is not necessarily
   * whoever finishes last: a prefetch that started while the flag was down,
   * then finished while a territory run was on page 1, used to lower it and
   * tell the journey tab the ingest was over. The tab read that as "no
   * prescriptions", and the real completion was then swallowed as a
   * `false → false` transition, so it never corrected itself.
   */
  private activeIngestCount = 0;

  /**
   * Which ingest is the live one.
   *
   * An ingest deliberately outlives the sync that started it, so the next sync
   * can begin while the previous run is still paging. Bumped when a run starts;
   * a run whose generation is no longer current stops at the next page boundary
   * and writes nothing further, so two runs never page or write the same
   * territory at once. This is also what makes a full sync correct: its purge
   * supersedes the in-flight run instead of letting it keep writing into the
   * emptied database and then stamp the snapshot as current.
   */
  private ingestGeneration = 0;

  /**
   * How many territory runs are paging right now.
   *
   * Only the territory path counts, not a prefetch: a single request is not
   * something to ask the rep to keep the app open for. Counted for the same
   * reason `activeIngestCount` is — a run that a newer one supersedes returns
   * at its next page boundary, and announcing its exit as the end of the
   * ingest would tell the rep the data is complete while the live run is on
   * page 1.
   */
  private territoryPagingCount = 0;

  /** Set between the two announcements, so neither is duplicated. */
  private completionAnnouncementPending = false;

  /**
   * Runs that have started but not yet settled, paging or not.
   *
   * Held alongside `territoryPagingCount` so completion is announced only when
   * nothing is either paging or on its way to it. A superseded run exits while
   * its successor is still deciding whether to page, and that successor may
   * never page at all — neither the paging count nor the run's generation can
   * tell those apart on its own.
   */
  private pendingIngestRunCount = 0;

  /**
   * Whether anything has actually landed since the rep was asked to keep the
   * app open. An abandoned or superseded run must not report the data as up to
   * date; the announcement is dropped in silence instead.
   */
  private ingestCompletedSinceAnnouncement = false;

  /**
   * Per-document write failures seen since the current paging run began.
   *
   * `bulkDocs` reports them per row rather than throwing, so the watermark
   * would otherwise advance over an HCP whose document never landed — and the
   * incremental window, once past the mapping, never offers that HCP again.
   */
  private failedWriteCount = 0;

  /**
   * HCPs written by a prefetch since the current paging run began.
   *
   * A prefetch writes outside the generation mechanism, so its HCP is in none
   * of the run's pages and a full snapshot's cleanup would tombstone the
   * document the rep is waiting on. Cleared when paging starts, so a genuine
   * removal is still purged by the next run.
   */
  private readonly prefetchedHcpMdmIds = new Set<string>();

  constructor(
    private readonly http: HttpClient,
    private readonly diskService: DiskService,
    private readonly deviceService: DeviceService,
    private readonly authService: AuthenticationService,
    private readonly authenticationDataService: AuthenticationDataService,
    private readonly notificationService: NotificationService,
    private readonly translate: TranslateService,
  ) {}

  /**
   * Tells the rep that trends are still coming in and that they can carry on
   * working, then tells them when the run lands.
   *
   * Called by the sync lane once everything it awaits has finished: this data
   * is fetched alongside the sync but never awaited by it, so on a first sync
   * of a large territory it routinely outlives the sync that started it, and
   * closing the app takes the remaining pages with it.
   *
   * Said only when a run is paging at this moment. Deliberately not armed for a
   * run that has yet to start: the trends run is scheduled a few seconds into
   * the sync tasks, so in the normal delta case it has already come and gone by
   * the time the sync ends, and arming on that would leave a pending
   * announcement to fire against some unrelated later ingest — a "keep the app
   * open" toast with no sync in sight.
   *
   * Held back until the sync's own success/failure toast has run its course,
   * which also means a run that lands inside that wait says nothing at all —
   * neither this nor the completion toast that pairs with it.
   */
  notifyIfIngestOutlastsSync(): void {
    if (this.territoryPagingCount === 0) return;
    setTimeout(() => {
      if (this.territoryPagingCount > 0) this.announceIngestStillRunning();
    }, WAIT_OUT_SYNC_TOAST_MS);
  }

  private announceIngestStillRunning(): void {
    if (this.completionAnnouncementPending) return;
    this.completionAnnouncementPending = true;
    this.ingestCompletedSinceAnnouncement = false;
    this.notificationService.notify(
      this.translate.instant('RX_TRENDS_SYNC_KEEP_APP_OPEN'),
      LOG,
      'top',
      ToastStyle.INFO,
      KEEP_APP_OPEN_TOAST_MS,
    );
  }

  /**
   * Closes the announcement once nothing is paging and nothing is on its way.
   *
   * Called from both ends — the last run to stop paging, and the last run to
   * settle — because a run can finish without ever having paged.
   */
  private announceIngestCompletedIfIdle(): void {
    if (!this.completionAnnouncementPending) return;
    if (this.territoryPagingCount > 0 || this.pendingIngestRunCount > 0) return;

    this.completionAnnouncementPending = false;
    // Silent on an ingest that was abandoned or superseded without landing
    // anything: the rep was told to keep the app open, and "up to date" would
    // be a claim about prescribing data that nothing on disk supports.
    if (!this.ingestCompletedSinceAnnouncement) {
      console.error(`${LOG} nothing landed since the rep was asked to keep the app open — no completion toast`);
      return;
    }
    this.notificationService.notify(this.translate.instant('RX_TRENDS_SYNC_COMPLETED'), LOG);
  }

  /**
   * Both counters move together for the territory path: the flag the journey
   * tab watches, and the one the announcements are keyed on.
   */
  private beginPagingRun(): void {
    this.territoryPagingCount++;
    this.failedWriteCount = 0;
    this.prefetchedHcpMdmIds.clear();
    this.beginIngestActivity();
  }

  private endPagingRun(): void {
    this.territoryPagingCount = Math.max(0, this.territoryPagingCount - 1);
    this.endIngestActivity();
    this.announceIngestCompletedIfIdle();
  }

  private beginIngestActivity(): void {
    this.activeIngestCount++;
    this.ingestInProgress$.next(true);
  }

  private endIngestActivity(): void {
    this.activeIngestCount = Math.max(0, this.activeIngestCount - 1);
    if (this.activeIngestCount === 0) this.ingestInProgress$.next(false);
  }

  /**
   * Emits its current value on subscribe, then on every change.
   *
   * `distinctUntilChanged` because a superseding run re-asserts `true` while the
   * flag is already `true`; the subscriber only cares about the transitions.
   */
  get isIngestInProgress$(): Observable<boolean> {
    return this.ingestInProgress$.asObservable().pipe(distinctUntilChanged());
  }

  private isCurrentGeneration(generation: number): boolean {
    return this.ingestGeneration === generation;
  }

  /** Whether a newer ingest has taken over from this one. */
  private isSuperseded(generation: number): boolean {
    return !this.isCurrentGeneration(generation);
  }

  private buildSeriesDocumentKey(hcpMdmId: string): string {
    return `${DB_KEY_PREFIXES.RX_TREND_SERIES}${hcpMdmId}`;
  }

  // ── API ─────────────────────────────────────────────────────────────────

  /**
   * Gateway base for the signed-in instance.
   *
   * The Rx endpoints are gateway routes under the `/api/edg` service prefix,
   * so they hang off `entryPointUrl` like every other `/api/...` endpoint —
   * **not** off `EdgeAnalyticsUrl`, which is a different service on its own
   * host whose paths carry no `/api` prefix.
   *
   * Deliberately not guarded against empty: under `localDevProxy` this is `''`
   * by design, leaving a relative `/api/edg/...` URL for the ng-serve proxy to
   * route. Matches how `search-config` and `market-scan` build their URLs.
   */
  private get gatewayBaseUrl(): string {
    return this.authService.userConfig?.activeInstance?.entryPointUrl ?? '';
  }

  /**
   * Positions to scope the **rows** request to, comma-separated.
   *
   * Only `/rx/trends` is position-scoped — `/rx/periods` takes `lastRefreshDate`
   * and nothing else, so this is deliberately not applied there.
   *
   * OMNI-61589 takes a **flat** list and expands nothing server-side, so a
   * manager rollup means sending child positions too. Ids go on the wire as
   * stored: the endpoint matched the lowercase ids every platform benchmark
   * was run with, so a test override and a real position send the same shape.
   *
   * Note: omitting the parameter entirely returns the whole snapshot
   * (1.87M rows on QA1). Returning an empty string here would do exactly
   * that, so the caller treats "no positions" as a reason not to fetch.
   */
  private resolvePositionIds(): string {
    try {
      const positions = this.authService.user?.positions ?? [];
      return positions.map((position) => position.ID).join(',');
    } catch (error) {
      console.error(`${LOG} failed to resolve position ids — ${describeError(error)}`);
      return '';
    }
  }

  /**
   * The `lastRefreshDate` to put on the wire, or `null` to omit it.
   *
   * Sent whenever the device holds a snapshot it can vouch for, so the server
   * can answer "is anything newer than this" — an unchanged `refreshDate`
   * returns no rows at all rather than the snapshot again.
   *
   * That same behaviour is why it is omitted when the device is rebuilding
   * rather than refreshing: a forced full sync, a snapshot stamped by an older
   * `SNAPSHOT_VERSION`, no series documents on disk, or no period definitions
   * to position them against. In all of them the rows are needed whether or not
   * the warehouse has moved since, and naming a snapshot would get an empty
   * response — leaving the rep with no rx trends until the next weekly load.
   *
   * The missing-definitions case is what lets a device that already lost them
   * heal itself: an earlier build wrote an unchanged snapshot's empty `periods`
   * over the stored ones (OMNI-62422), and a device in that state can vouch for
   * its rows but has no windows to position them in — so it has to ask for the
   * definitions again rather than name a snapshot and be told nothing is new.
   */
  private resolveLastRefreshDateToSend(
    storedRefreshDate: string | null,
    isStaleStamp: boolean,
    storedSeriesCount: number,
    forceFullSync: boolean,
    storedPeriodCount: number,
  ): string | null {
    if (forceFullSync || isStaleStamp || storedSeriesCount === 0 || storedPeriodCount === 0) {
      return null;
    }
    return storedRefreshDate;
  }

  /**
   * `GET /rx/periods` — the WK1..WK26 / M1..M12 window definitions.
   *
   * Must be read before the rows: the trend arrays are positional and shift on
   * every weekly refresh, so axis labels cannot be derived without this.
   *
   * `lastRefreshDate` carries the snapshot already on disk, so `hasNewData`
   * answers "is there anything newer than what this device holds" rather than
   * degrading to "the server has some data". Omitted when the device has no
   * snapshot it can vouch for — see `resolveLastRefreshDateToSend`.
   *
   * The local `refreshDate` comparison in `runIngestIfDataChanged` remains the
   * skip condition rather than `hasNewData`: it is the check that holds on a
   * first ingest, where nothing is stored to name, and it does not depend on
   * how the server reads an absent parameter. `hasNewData` agreeing with it is
   * logged, not relied on.
   */
  private async fetchPeriods(lastRefreshDate: string | null): Promise<RxPeriodsApiResponse | null> {
    if (this.deviceService.isDeviceRealOffline) return null;

    const query = lastRefreshDate ? `?lastRefreshDate=${encodeURIComponent(lastRefreshDate)}` : '';
    const url = this.gatewayBaseUrl + Endpoints.rxTrends.PERIODS + query;
    const startedAt = performance.now();

    try {
      const response = (await firstValueFrom(this.http.get<RxPeriodsApiResponse>(url))) ?? null;
      return response;
    } catch (error) {
      console.error(
        `${LOG} /rx/periods request FAILED after ${Math.round(performance.now() - startedAt)}ms — ${describeError(error)}`,
      );
      return null;
    }
  }

  /**
   * `GET /rx/trends` — one page of rows, scoped to the caller's positions.
   *
   * `lastRefreshDate` is the same value sent to `/rx/periods`, so the two
   * responses are scoped identically and the mid-load `refreshDate` mismatch
   * check below stays meaningful. `hasNewData` is still noted rather than acted
   * on — see the loop.
   *
   * It never trims what comes back: the response is either the whole snapshot
   * or nothing at all, since an unchanged `refreshDate` returns no rows. Both a
   * refresh and a delta sync therefore fetch in full (rule 5 — replace, never
   * merge), and the caller only reaches this method when the server's
   * `refreshDate` differs from the one it named.
   *
   * `includeZeroRows=false` keeps the endpoint's default, which drops a row
   * whose 26 weeks *and* 12 months are all zero. What that costs, measured on
   * the 2026-07-21 extract: 7,335 of 217,677 rows are dropped, one product
   * disappears entirely from 3,369 HCPs, and 2,764 HCPs come back with nothing
   * at all. It also drops NRx-only rows, so a product with a healthy TRx can
   * lose its NRx and Refills lines.
   *
   * The consequence for the UI: an absent row means "no such (hcp, product)"
   * *or* "all buckets were zero", and the two are indistinguishable here. The
   * adapter still treats a missing bucket as unknown rather than 0, so an
   * all-zero product reads as no data rather than as a flat line at zero.
   *
   * `totalCount` is only returned on page 0 and is null thereafter, so it is
   * logged rather than used to drive the loop; `hasMore` is the authority.
   */
  private async fetchRowsPage(
    pageNumber: number,
    pageSize: number,
    lastRefreshDate: string | null,
    lastSyncDate: number | null,
  ): Promise<RxTrendsApiResponse | null> {
    if (this.deviceService.isDeviceRealOffline) return null;

    const positionIds = this.resolvePositionIds();
    const parameters = [
      `positionIds=${encodeURIComponent(positionIds)}`,
      `pageNumber=${pageNumber}`,
      `pageSize=${pageSize}`,
      'includeZeroRows=false',
    ];
    if (lastRefreshDate) parameters.push(`lastRefreshDate=${encodeURIComponent(lastRefreshDate)}`);
    // What makes a membership change visible: the server checks the position
    // table for mappings made since this watermark, so an HCP added to a
    // position the rep already holds comes back even though the extract itself
    // has not moved. Ignored by the server once the warehouse has.
    if (lastSyncDate) parameters.push(`lastSyncDate=${lastSyncDate}`);

    const url = `${this.gatewayBaseUrl}${Endpoints.rxTrends.TRENDS}?${parameters.join('&')}`;
    const startedAt = performance.now();

    try {
      return (await firstValueFrom(this.http.get<RxTrendsApiResponse>(url))) ?? null;
    } catch (error) {
      console.error(
        `${LOG} /rx/trends page ${pageNumber} request FAILED after ${Math.round(performance.now() - startedAt)}ms — ${describeError(error)}`,
      );
      return null;
    }
  }

  /**
   * `GET /rx/trends/mdm?mdmIds=` — rows for named HCPs, outside the position
   * scoping the territory fetch is subject to.
   *
   * Un-paged and unwatermarked: a single HCP is a handful of rows, and the
   * caller wants them whether or not the warehouse has moved — naming a
   * snapshot would answer "nothing newer" with nothing at all.
   *
   * `mdmIds` takes a list, so adding several contacts at once could be one
   * call. Kept to one id per call while the only caller adds one contact at a
   * time; batching is a caller-side change, not a contract one.
   */
  private async fetchRowsForHcp(hcpMdmId: string): Promise<RxTrendsApiResponse | null> {
    const url = `${this.gatewayBaseUrl}${Endpoints.rxTrends.TRENDS_BY_MDM}?mdmIds=${encodeURIComponent(hcpMdmId)}`;
    const startedAt = performance.now();

    try {
      const response = (await firstValueFrom(this.http.get<RxTrendsApiResponse>(url))) ?? null;
      return response;
    } catch (error) {
      console.error(
        `${LOG} /rx/trends/mdm ${hcpMdmId} FAILED after ${Math.round(performance.now() - startedAt)}ms — ${describeError(error)}`,
      );
      return null;
    }
  }

  /**
   * Caches one HCP's series on demand, for an HCP the territory snapshot does
   * not cover — a contact just added to the rep's position from global search.
   *
   * The mapping is made server-side, but the stored snapshot is only rebuilt
   * when the warehouse `refreshDate` moves, so without this the journey tab
   * has nothing for that contact until the next weekly refresh. Called while
   * the device is still online at add time rather than when the tab opens,
   * since the rep can add a contact and go offline before ever opening it.
   *
   * Writes only when the response carries the same `refreshDate` as the stored
   * periods. The arrays are positional against those period definitions, so
   * rows from a newer snapshot would be read against the older windows — WK1
   * meaning a different week. On a mismatch nothing is written and the next
   * ingest brings the HCP in through the territory path, which it now covers.
   *
   * @returns whether a document was written.
   */
  async cacheTrendsForHcp(hcpMdmId: string): Promise<boolean> {
    if (!hcpMdmId || this.deviceService.isDeviceRealOffline || !this.authService.hasFeatureAction(FeatureActionsMap.RX_TRENDS)) {
      return false;
    }
    if (!this.authenticationDataService.hasValidCachedInstanceToken) return false;


    // Raised for the same reason an ingest raises it: the journey tab re-reads
    // its contact's series when this falls back to false, so a tab already open
    // on a contact whose rows land mid-render fills itself in. Without it the
    // rep has to leave the tab and come back — which is exactly what happens
    // when Journey is the configured default tab and the prefetch is still in
    // flight as it renders.
    this.beginIngestActivity();

    try {
      const storedPeriods = await this.getStoredPeriodsDocument();
      if (!storedPeriods?.periods?.length || !storedPeriods.refreshDate) {
        return false;
      }

      const response = await this.fetchRowsForHcp(hcpMdmId);
      if (!response?.rows?.length) return false;

      if (response.refreshDate !== storedPeriods.refreshDate) {
        return false;
      }

      const savedDocumentCount = await this.saveSeriesToLocalDatabase(
        storedPeriods.periods,
        response.rows,
        response.refreshDate,
      );
      // Held so a full snapshot already paging — which was served its pages
      // before this HCP was mapped, and so cannot contain it — does not
      // tombstone the document as one that fell out of the territory.
      if (savedDocumentCount > 0) this.prefetchedHcpMdmIds.add(hcpMdmId);
      return savedDocumentCount > 0;
    } finally {
      // Lowered after the write, so the re-read it triggers finds the document.
      this.endIngestActivity();
    }
  }

  // ── Ingest ──────────────────────────────────────────────────────────────

  /**
   * Fetches and saves every page of rows for the given `refreshDate`, one page
   * at a time.
   *
   * Each page is mapped, written and discarded before the next is requested,
   * so peak memory is bounded by `ROWS_PER_PAGE` rather than by the dataset.
   * That is the whole point of the paged contract: buffering every page into
   * one array first — and then handing that array to the adapter, which builds
   * a second full copy — would hold the raw rows and the mapped series in
   * memory simultaneously. At 10k HCPs x 15 products x 2 rxTypes that is
   * hundreds of MB on a device whose WebView heap is a fraction of it.
   *
   * Aborts if a page comes back with a different `refreshDate` than the
   * periods fetch that started the ingest — the source refreshed mid-ingest,
   * so every row read since is positioned against period windows that no
   * longer match. `completed: false` then keeps the periods document
   * unstamped, so the next open retries rather than treating a half-written
   * territory as current.
   */
  private async ingestAllPages(
    generation: number,
    periodsResponse: RxPeriodsApiResponse,
    lastRefreshDate: string | null,
    lastSyncDate: number | null,
  ): Promise<{
    savedDocumentCount: number;
    completed: boolean;
    writtenHcpMdmIds: Set<string>;
    /**
     * `refreshDate` as reported by the **rows** responses — the snapshot the
     * stored documents actually came from, and what gets stamped and sent as
     * `lastRefreshDate` next time. Every page is checked equal to the periods
     * response before any of it is written, so this is that same value proven
     * against the data rather than assumed from a parallel response.
     */
    refreshDate: string | null;
    /**
     * The server's own verdict on what these rows are, from page 0. Absent on a
     * server predating the flag, which reads as a full snapshot — the behaviour
     * before incremental responses existed.
     */
    incremental: boolean | undefined;
    /** The server's watermark from page 0, stored for the next call. */
    syncDate: number | null;
  }> {
    const expectedRefreshDate = periodsResponse.refreshDate;
    let observedRefreshDate: string | null = null;
    let observedIncremental: boolean | undefined;
    let observedSyncDate: number | null = null;
    let pageNumber = 0;
    let hasMore = true;
    let savedDocumentCount = 0;
    /** Rows held back at a page boundary — see `splitTrailingHcp`. */
    let carriedRows: RxTrendsApiRow[] = [];
    /** Detects an HCP arriving in two pages; see `saveSeriesToLocalDatabase`. */
    const writtenHcpMdmIds = new Set<string>();
    const ingestStartedAt = performance.now();

    while (hasMore) {
      if (this.isSuperseded(generation)) {
        return {
          savedDocumentCount,
          completed: false,
          writtenHcpMdmIds,
          refreshDate: null,
          incremental: observedIncremental,
          syncDate: observedSyncDate,
        };
      }

      const page = await this.fetchRowsPage(pageNumber, ROWS_PER_PAGE, lastRefreshDate, lastSyncDate);

      // Re-checked after the fetch: a newer ingest — or a full sync's purge —
      // can start during it, and writing this page afterwards would put rows
      // from a superseded snapshot into a database the live run is rebuilding.
      if (this.isSuperseded(generation)) {
        return {
          savedDocumentCount,
          completed: false,
          writtenHcpMdmIds,
          refreshDate: null,
          incremental: observedIncremental,
          syncDate: observedSyncDate,
        };
      }

      if (!page) {
        console.error(
          `${LOG} ingest ABANDONED at page ${pageNumber} after ${Math.round(performance.now() - ingestStartedAt)}ms — ${savedDocumentCount} documents written`,
        );
        return {
          savedDocumentCount,
          completed: false,
          writtenHcpMdmIds,
          refreshDate: null,
          incremental: observedIncremental,
          syncDate: observedSyncDate,
        };
      }

      // `hasNewData` is noted, not acted on. On a full pull it is whatever the
      // server does with an absent parameter, and aborting on it would let one
      // plausible reading ("no lastRefreshDate, so nothing is newer") silently
      // disable the feature. On a scoped pull the caller has already decided to
      // ingest from the local refreshDate comparison, and a `false` part-way
      // through paging would abandon a half-written territory.

      if (page.refreshDate !== expectedRefreshDate) {
        console.error(
          `${LOG} ABORT: refreshDate mismatch (periods ${expectedRefreshDate}, rows ${page.refreshDate}) — warehouse is mid-load`,
        );
        return {
          savedDocumentCount,
          completed: false,
          writtenHcpMdmIds,
          refreshDate: null,
          incremental: observedIncremental,
          syncDate: observedSyncDate,
        };
      }

      observedRefreshDate = page.refreshDate;
      if (pageNumber === 0) {
        observedIncremental = page.incremental;
        observedSyncDate = page.syncDate ?? null;
      }
      hasMore = page.hasMore;
      pageNumber++;

      const pageRows = page.rows ?? [];
      const rows = carriedRows.length ? carriedRows.concat(pageRows) : pageRows;
      const { readyToSave, carried } = hasMore
        ? this.splitTrailingHcp(rows)
        : { readyToSave: rows, carried: [] };
      carriedRows = carried;

      const documentsWritten = await this.saveSeriesToLocalDatabase(
        periodsResponse.periods,
        readyToSave,
        observedRefreshDate,
        writtenHcpMdmIds,
      );
      savedDocumentCount += documentsWritten;
    }

    return {
      savedDocumentCount,
      completed: true,
      writtenHcpMdmIds,
      refreshDate: observedRefreshDate,
      incremental: observedIncremental,
      syncDate: observedSyncDate,
    };
  }

  /**
   * Deletes stored HCP documents that the new snapshot did not contain.
   *
   * OMNI-61589's rule 5: a new snapshot replaces the local copy outright and
   * is never delta-merged, because every refresh shifts what WK1/M1 mean.
   * Writing per-HCP documents only overwrites the HCPs present *this* time —
   * an HCP that dropped out of the rep's territory, or fell below the
   * zero-row filter, would otherwise keep serving values positioned against
   * period windows that no longer exist.
   *
   * Only ever called after a completed ingest, so a partial page never
   * deletes the rest of the territory.
   */
  private async removeDocumentsOutsideSnapshot(writtenHcpMdmIds: Set<string>): Promise<number> {
    const prefixLength = DB_KEY_PREFIXES.RX_TREND_SERIES.length;
    const storedDocumentIds = await this.listStoredDocumentIds();
    const staleDocumentIds = storedDocumentIds.filter(
      (documentId) => !writtenHcpMdmIds.has(documentId.slice(prefixLength)),
    );
    if (!staleDocumentIds.length) return 0;

    let deletedCount = 0;
    for (let batchStart = 0; batchStart < staleDocumentIds.length; batchStart += HCPS_PER_WRITE_BATCH) {
      const idsInBatch = staleDocumentIds.slice(batchStart, batchStart + HCPS_PER_WRITE_BATCH);
      const allDocsResponse: any = await this.diskService.batchFetch(
        { keys: idsInBatch, include_docs: false },
        false,
      );

      const tombstones = (allDocsResponse?.rows ?? [])
        .filter((row) => row?.id && row?.value?.rev && !row.value.deleted)
        .map((row) => ({ _id: row.id, _rev: row.value.rev, _deleted: true }));

      if (tombstones.length) {
        await this.diskService.bulk(tombstones);
        deletedCount += tombstones.length;
      }
    }

    return deletedCount;
  }

  /**
   * Splits off the trailing HCP's rows so they can be carried into the next
   * page.
   *
   * One HCP's rows straddle a page boundary — `0022621` alone has 6 rows in
   * the sample. Since a document holds *all* of an HCP's products, writing
   * that HCP from a partial page and then again from the next would leave the
   * second write holding only the second page's products.
   *
   * This relies on rows for one HCP arriving contiguously. That is how the
   * source orders them today but is not a documented guarantee, which is why
   * `saveSeriesToLocalDatabase` also checks for an HCP it has already written.
   */
  private splitTrailingHcp(
    rows: RxTrendsApiRow[],
  ): { readyToSave: RxTrendsApiRow[]; carried: RxTrendsApiRow[] } {
    if (!rows.length) return { readyToSave: rows, carried: [] };

    const trailingHcpId = rows[rows.length - 1]?.hcpId;
    let boundary = rows.length;
    while (boundary > 0 && rows[boundary - 1]?.hcpId === trailingHcpId) boundary--;

    return { readyToSave: rows.slice(0, boundary), carried: rows.slice(boundary) };
  }

  /**
   * Groups rows by HCP via the adapter, then converts and saves one document
   * per HCP.
   *
   * Batched rather than one `updateOrInsert` per HCP: at territory scale that
   * would be one PouchDB transaction per contact — 5,000 of them — which is
   * the write pattern behind this app's history of PouchDB crashes.
   * Documents are built, revision-stamped and written a batch at a time, so
   * peak memory is bounded by the batch rather than by the payload.
   *
   * @returns how many documents were written.
   */
  async saveSeriesToLocalDatabase(
    periods: RxApiPeriodDefinition[],
    rows: RxTrendsApiRow[],
    dataAsOfDate: string | null,
    writtenHcpMdmIds?: Set<string>,
  ): Promise<number> {
    if (!rows?.length) return 0;

    const seriesByHcpId = mapRxApiRowsToProductSeriesByHcp(periods, rows);
    const allHcpMdmIds = [...seriesByHcpId.keys()];
    let savedDocumentCount = 0;

    // A document is rebuilt wholesale, so an HCP written by an earlier page
    // and again by a later one silently loses the earlier page's products.
    // `splitTrailingHcp` prevents that for contiguous rows; this catches the
    // case it cannot — the source not grouping an HCP's rows together — and
    // says so rather than shipping a chart that is quietly missing products.
    if (writtenHcpMdmIds) {
      for (const hcpMdmId of allHcpMdmIds) {
        if (writtenHcpMdmIds.has(hcpMdmId)) {
          console.error(
            `${LOG} hcp ${hcpMdmId} arrived in more than one page — rows are not grouped by HCP, so this document now holds only its last page's products`,
          );
        }
        writtenHcpMdmIds.add(hcpMdmId);
      }
    }

    for (let batchStart = 0; batchStart < allHcpMdmIds.length; batchStart += HCPS_PER_WRITE_BATCH) {
      const hcpMdmIdsInBatch = allHcpMdmIds.slice(batchStart, batchStart + HCPS_PER_WRITE_BATCH);
      const documentsToWrite: RxTrendSeriesDocument[] = hcpMdmIdsInBatch.map((hcpMdmId) => ({
        _id: this.buildSeriesDocumentKey(hcpMdmId),
        hcpMdmId,
        dataAsOfDate,
        series: seriesByHcpId.get(hcpMdmId) ?? [],
      }));

      if (!documentsToWrite.length) continue;

      await this.attachExistingRevisions(documentsToWrite);
      await this.writeDocumentBatch(documentsToWrite);
      savedDocumentCount += documentsToWrite.length;
    }

    return savedDocumentCount;
  }

  /**
   * Stamps `_rev` onto documents that already exist.
   *
   * `bulkDocs` rejects an update that arrives without the document's current
   * revision, so without this every save after the first would come back as a
   * conflict per document and write nothing. Ids only — the existing documents
   * are never loaded, just their revisions.
   */
  private async attachExistingRevisions(documents: RxTrendSeriesDocument[]): Promise<void> {
    const allDocsResponse: any = await this.diskService.batchFetch(
      { keys: documents.map((document) => document._id), include_docs: false },
      false,
    );

    const revisionByDocumentId = new Map<string, string>();
    for (const row of allDocsResponse?.rows ?? []) {
      // Documents that do not exist come back as rows carrying an error, and
      // deleted ones as tombstones — neither should contribute a revision.
      if (row?.id && row?.value?.rev && !row.value.deleted) {
        revisionByDocumentId.set(row.id, row.value.rev);
      }
    }

    for (const document of documents) {
      const existingRevision = revisionByDocumentId.get(document._id);
      if (existingRevision) document._rev = existingRevision;
    }
  }

  /**
   * Writes one batch and surfaces per-document failures.
   *
   * `bulkDocs` reports failures per row rather than throwing, so an unchecked
   * call would lose a conflict or a rejected document silently — the save would
   * look successful while that contact's chart stayed empty.
   */
  private async writeDocumentBatch(documents: RxTrendSeriesDocument[]): Promise<void> {
    const writeResults: any[] = (await this.diskService.bulk(documents)) ?? [];
    const failedWrites = writeResults.filter((result) => result && result.error);

    if (failedWrites.length) {
      this.failedWriteCount += failedWrites.length;
      console.error(
        `${LOG} ${failedWrites.length}/${documents.length} documents failed to write — ${failedWrites
          .slice(0, 5)
          .map((failure) => `${failure.id ?? 'unknown id'}: ${describeError(failure.error)}`)
          .join('; ')}`,
      );
    }
  }

  private async getStoredPeriodsDocument(): Promise<RxTrendPeriodsDocument | null> {
    try {
      const document = await this.diskService.retrieve(PERIODS_DOCUMENT_ID, true);
      return document ?? null;
    } catch (error) {
      console.error(`${LOG} failed to read stored periods — ${describeError(error)}`);
      return null;
    }
  }

  /**
   * @param refreshDate the date the stored rows reported — see the call site.
   *                    The period *definitions* still come from the periods
   *                    response, since only that endpoint returns them.
   */
  private async savePeriodsDocument(
    periodsResponse: RxPeriodsApiResponse,
    refreshDate: string,
  ): Promise<void> {
    await this.diskService.updateOrInsert(PERIODS_DOCUMENT_ID, (document) => ({
      refreshDate,
      // Carried over when the response brings none: an unchanged snapshot
      // answers with an empty list, and writing that through would discard the
      // only copy of the window definitions on the device — leaving nothing for
      // the fallback above to stand in with on the next run.
      periods: periodsResponse.periods?.length ? periodsResponse.periods : (document?.periods ?? []),
      snapshotVersion: SNAPSHOT_VERSION,
      // Carried over: the watermark is recorded before this runs, and rebuilding
      // the document from the response alone would drop it.
      lastSyncDate: document?.lastSyncDate,
    }));
  }

  /**
   * Records the server's watermark for the next call's `lastSyncDate`. Written
   * on every completed run, including one that returned no rows — the server
   * advances `syncDate` regardless, and keeping the older one would make it
   * re-answer the same window for ever.
   */
  private async saveLastSyncDate(lastSyncDate: number): Promise<void> {
    try {
      await this.diskService.updateOrInsert(PERIODS_DOCUMENT_ID, (document) => ({
        ...document,
        lastSyncDate,
      }));
    } catch (error) {
      console.error(`${LOG} failed to record lastSyncDate — ${describeError(error)}`);
    }
  }

  /**
   * True when documents are on disk but no snapshot was ever stamped — the
   * signature of an ingest that abandoned part-way (offline mid-paging) before
   * any complete snapshot existed. The territory is a partial one, so an HCP
   * with no series may simply be in a page that never landed, and "no data"
   * would be a lie.
   *
   * A later abandoned ingest cannot produce this: the previous snapshot's
   * documents survive per-HCP writes, so every HCP still has a series, at worst
   * a refresh behind — which the card already accepts.
   */
  async isSnapshotPartial(): Promise<boolean> {
    const storedPeriods = await this.getStoredPeriodsDocument();
    if (storedPeriods?.refreshDate) return false;

    return (await this.listStoredDocumentIds()).length > 0;
  }

  /** Keyed read. One document, no scan, no cross-HCP aggregation. */
  async getSeriesByHcpMdmId(hcpMdmId: string): Promise<RxTrendSeriesDocument | null> {
    if (!hcpMdmId) return null;
    try {
      const document = await this.diskService.retrieve(this.buildSeriesDocumentKey(hcpMdmId), true);
      return document ?? null;
    } catch (error) {
      console.error(`${LOG} failed to read series — ${describeError(error)}`);
      return null;
    }
  }

  /**
   * Reads one contact's series. The payload's `hcpId` is the contact's MDM id,
   * so `Contact.indskr_mdmid` is the only lookup key — no match means no data
   * for that contact.
   */
  async getSeriesForContact(hcpMdmId?: string): Promise<RxTrendSeriesDocument | null> {
    // Joins an ingest already running, and never starts one: reading a
    // contact must not fetch or write. Sync owns the refresh — see
    // `syncRxTrends`, whose delayed run this used to race.
    //
    // This call site is older than the API. It began as
    // `ensureSampleDataIngested()`, parsing a bundled JSON fixture, where
    // triggering it from a read cost nothing; OMNI-61589 swapped the fixtures
    // for the real endpoints and left the trigger in place, which made opening
    // a contact page both endpoints and rewrite that contact's document. When
    // the snapshot had not moved, the response carried no period definitions
    // and the rewrite blanked the very anchors the chart needs, so viewing a
    // contact corrupted it (OMNI-62422).
    //
    // Still concurrent with the disk read: when sync's ingest is genuinely in
    // flight, waiting for it in series would put it on the critical path of
    // the chart render for no benefit.
    const [documentBeforeIngest, didDataChange] = await Promise.all([
      this.getSeriesByHcpMdmId(hcpMdmId),
      this.joinIngestAlreadyRunning(),
    ]);

    // Stale figures are never served: if the ingest rewrote anything, the
    // document read alongside it may predate that write, so it is discarded
    // and re-read rather than rendered.
    if (!didDataChange) return documentBeforeIngest;
    return this.getSeriesByHcpMdmId(hcpMdmId);
  }

  /**
   * Sync entry point — started a few seconds into the sync tasks, alongside
   * them, and never awaited. The rows must be on disk before the rep loses
   * connectivity, which a journey-tab trigger cannot guarantee.
   *
   * Clears the session guard first: a rep can keep the app open across several
   * delta syncs, and the cached promise would make every sync after the first a
   * no-op. Safe even mid-paging — the new run takes over the generation and the
   * previous one stops at its next page boundary.
   *
   * Errors are contained: this is the lowest priority data in the app and it
   * must never fail a sync.
   */
  async syncRxTrends(forceFullSync = false): Promise<void> {
    if (this.deviceService.isDeviceRealOffline || !this.authService.hasFeatureAction(FeatureActionsMap.RX_TRENDS)) return;

    // Rides the session's existing token: a background fetch must never drive an
    // MSAL exchange, which can need interaction. Deferred to the next sync.
    if (!this.authenticationDataService.hasValidCachedInstanceToken) return;

    this.ingestionPromise = undefined;
    const startedAt = performance.now();
    try {
      await this.ensureDataIngested(forceFullSync);
    } catch (error) {
      console.error(
        `${LOG} sync ingest failed after ${Math.round(performance.now() - startedAt)}ms — ${describeError(error)}`,
      );
    } finally {
      // Also checked here, not only when a run stops paging: this exit covers
      // the offline and database-only returns above, which start no run at all
      // and so never reach the run-settled hook.
      this.announceIngestCompletedIfIdle();
    }
  }

  /**
   * Runs the ingest at most once per session, shared by concurrent callers.
   *
   * Sync is the only caller that may start one — readers join an in-flight run
   * through `joinIngestAlreadyRunning` instead, so displaying data can never
   * fetch or write it.
   *
   * @returns whether the ingest actually rewrote anything on disk.
   */
  /**
   * The in-flight ingest's result, or `false` when none is running.
   *
   * For readers, which must never trigger a fetch or a write of their own. The
   * answer means the same thing either way — "did anything on disk change
   * while I was reading" — so a caller still knows to discard a document it
   * may have read from under a write.
   */
  private joinIngestAlreadyRunning(): Promise<boolean> {
    return this.ingestionPromise ?? Promise.resolve(false);
  }

  private ensureDataIngested(forceFullSync = false): Promise<boolean> {
    if (!this.ingestionPromise) {
      const generation = ++this.ingestGeneration;
      // Counted from here rather than from the first page, because the decision
      // work in front of paging — a periods call and an index scan — is exactly
      // the window in which the paging count reads as idle while a run is very
      // much on its way. Announcing completion in that window would either fire
      // early or, for a run that never pages at all, never fire.
      this.pendingIngestRunCount++;
      const run = this.runIngestIfDataChanged(generation, forceFullSync).catch((error) => {
        console.error(`${LOG} ingest failed — ${describeError(error)}`);
        // Cleared so a later open can retry rather than being stuck on a
        // transient failure — but only while this run is still the live one. A
        // superseded run rejecting late would otherwise clear the promise its
        // successor is running under, letting a third ingest start alongside it.
        if (this.isCurrentGeneration(generation)) {
          this.ingestionPromise = undefined;
        }
        return false;
      });
      this.ingestionPromise = run.finally(() => {
        this.pendingIngestRunCount = Math.max(0, this.pendingIngestRunCount - 1);
        this.announceIngestCompletedIfIdle();
      });
    }
    return this.ingestionPromise;
  }

  /**
   * Fetches periods, then rows page by page, replaces the local copy, and
   * stamps the snapshot — skipping the whole thing when the server reports
   * nothing newer than what is already stored.
   *
   * The stored `refreshDate` is sent as `lastRefreshDate`, so `hasNewData`
   * answers "is there anything newer than what this device holds" rather than
   * being a server-global flag. A `refreshDate` equality check backs it up,
   * since the very first ingest has nothing stored to compare against.
   *
   * @returns whether any document was rewritten.
   */
  private async runIngestIfDataChanged(generation: number, forceFullSync = false): Promise<boolean> {
    return this.resolveAndIngestSnapshot(generation, forceFullSync);
  }

  private async resolveAndIngestSnapshot(generation: number, forceFullSync: boolean): Promise<boolean> {
    const positionIds = this.resolvePositionIds();
    if (!positionIds) {
      // Omitting positionIds entirely returns the whole snapshot — 1.87M rows
      // on QA1 — so no positions means do not fetch, not fetch everything.
      return false;
    }

    const storedPeriods = await this.getStoredPeriodsDocument();
    const storedVersion = storedPeriods?.snapshotVersion;
    const lastRefreshDate = storedPeriods?.refreshDate ?? null;

    // A stamp from an older version cannot vouch for the rows on disk — see
    // SNAPSHOT_VERSION. Ignoring it forces a full pull exactly once.
    const staleStamp = !!storedPeriods && storedVersion !== SNAPSHOT_VERSION;

    // The stamp alone does not prove the rows are on disk — a purge, a failed
    // write or a cleared database can leave it behind. Verified before trusting it.
    // Counted before the periods fetch because it decides whether this device has
    // a snapshot worth naming on the wire, not only whether a skip is safe.
    const storedSeriesCount = staleStamp ? 0 : (await this.listStoredDocumentIds()).length;

    const lastRefreshDateToSend = this.resolveLastRefreshDateToSend(
      lastRefreshDate,
      staleStamp,
      storedSeriesCount,
      forceFullSync,
      storedPeriods?.periods?.length ?? 0,
    );

    const periodsResponse = await this.fetchPeriods(lastRefreshDateToSend);
    if (!periodsResponse) return false;

    // No local skip on a matching `refreshDate`. Position membership can change
    // without the warehouse moving — an HCP added to a position the rep already
    // holds leaves both `refreshDate` and `positionIds` identical — so the rows
    // call is the only thing that can answer "is there anything new for me",
    // and it has to be made before the answer exists.
    //
    // A named snapshot the warehouse has not moved past comes back with no
    // period definitions, so the stored ones stand in: unchanged means the
    // WK/M windows are unchanged, and any rows that follow are positioned
    // against exactly these definitions.
    //
    // Keyed off `periods` rather than `refreshDate`, which is what this
    // originally tested: the response to an unchanged snapshot carries its
    // `refreshDate` but an empty `periods`, so the stamp was always truthy and
    // the fallback never ran. The empty list then reached the mapper, where the
    // bucket counts have constants to fall back on but the anchors have
    // nothing — leaving every series full of values with no window to position
    // them in, which charts as nothing at all (OMNI-62422).
    //
    // Both halves are required: the definitions to position rows against, and
    // the stamp the per-page refreshDate match below is checked against.
    const effectivePeriods: RxPeriodsApiResponse | null =
      periodsResponse.periods?.length && periodsResponse.refreshDate
        ? periodsResponse
        : storedPeriods?.refreshDate && storedPeriods.periods?.length
          ? { refreshDate: storedPeriods.refreshDate, hasNewData: false, periods: storedPeriods.periods }
          : null;

    // Nothing from the server and nothing on disk to position rows against —
    // the device has never held a snapshot, so there is nothing to ingest.
    if (!effectivePeriods) return false;

    // Sent whenever a snapshot is named, and omitted with it on a rebuild: a
    // rebuild wants every row the positions are entitled to, and naming a
    // watermark there would narrow it to whatever was mapped since, leaving the
    // territory permanently short of the rest.
    const lastSyncDateToSend = lastRefreshDateToSend ? (storedPeriods?.lastSyncDate ?? null) : null;


    return this.ingestSnapshot(generation, effectivePeriods, lastRefreshDateToSend, lastSyncDateToSend);
  }

  /**
   * Pages the snapshot in, drops what it did not contain, and stamps it.
   *
   * Split from the decision logic above so the in-progress flag is raised only
   * for the part that fetches and writes — the checks that precede it are one
   * small `/rx/periods` call and a disk read, and reporting those as an ingest
   * in progress would flash a syncing state on every journey-tab open. Raised
   * and lowered here as a pair, so an overlapping run cannot lower a flag it
   * did not raise.
   */
  private async ingestSnapshot(
    generation: number,
    periodsResponse: RxPeriodsApiResponse,
    lastRefreshDateToSend: string | null,
    lastSyncDate: number | null,
  ): Promise<boolean> {
    try {
      this.beginPagingRun();
      return await this.pageAndReplaceSnapshot(generation, periodsResponse, lastRefreshDateToSend, lastSyncDate);
    } finally {
      this.endPagingRun();
    }
  }

  private async pageAndReplaceSnapshot(
    generation: number,
    periodsResponse: RxPeriodsApiResponse,
    lastRefreshDateToSend: string | null,
    lastSyncDate: number | null,
  ): Promise<boolean> {
    const { savedDocumentCount, completed, writtenHcpMdmIds, refreshDate, incremental, syncDate } =
      await this.ingestAllPages(
        generation,
        periodsResponse,
        lastRefreshDateToSend,
        lastSyncDate,
      );

    if (!completed) return savedDocumentCount > 0;

    // Every page landed, so the run is entitled to report itself as finished.
    this.ingestCompletedSinceAnnouncement = true;

    // An empty response is only acted on when the server said what it was.
    // `incremental` is optional on the wire, and a response that omits it while
    // carrying no rows is indistinguishable from a snapshot that should have
    // held the whole territory and didn't — so nothing is deleted, stamped or
    // advanced on that guess. An explicit `incremental: false` is trusted and
    // falls through to the replace path below, which is what still purges a
    // territory that genuinely lost every HCP.
    if (!writtenHcpMdmIds.size && incremental === undefined) {
      console.error(
        `${LOG} snapshot carried no rows and no incremental flag — disk, stamp and watermark all left as they are`,
      );
      return false;
    }

    // Advanced on every completed run, empty ones included: the server moves
    // `syncDate` on regardless, and keeping the older watermark would make it
    // re-answer the same window for ever.
    //
    // Held back when any document failed to write, though. `bulkDocs` reports
    // those per row instead of throwing, so advancing here would put a
    // never-written HCP behind the watermark and the incremental window would
    // never offer it again — the full-snapshot path used to re-deliver it, the
    // incremental one has nothing that would.
    if (syncDate && !this.failedWriteCount) {
      await this.saveLastSyncDate(syncDate);
    } else if (syncDate) {
      console.error(
        `${LOG} watermark not advanced to ${syncDate} — ${this.failedWriteCount} document(s) failed to write, so the next run re-asks for this window`,
      );
    }

    // The server's verdict, not ours: these rows are what changed since the
    // watermark rather than the territory as it now stands.
    if (incremental) {
      // Nothing came back means nothing new for these positions — not that the
      // territory is empty, and the two are indistinguishable on the wire. The
      // documents on disk are left exactly as they are; cleaning up here would
      // read an unchanged snapshot as a territory that lost every HCP.
      if (!writtenHcpMdmIds.size) {
        return false;
      }

      // Rows: newly-mapped HCPs only, merged rather than replacing. They
      // describe what changed, not the territory, so an absence means "not
      // newly mapped" rather than "no longer in it" and the cleanup below would
      // delete everything else. Removals keep being handled by the next full
      // snapshot, which does purge. Nothing to re-stamp either — the
      // refreshDate on disk is the one these rows were positioned against.
      return savedDocumentCount > 0;
    }

    // Rule 5: replace, never merge. Anything not in this snapshot is dropped
    // before the new refreshDate is stamped.
    //
    // Safe because the server declared this a whole snapshot: the pages just
    // written describe the entire territory, so an absence really is "no longer
    // in it".
    // HCPs a prefetch wrote while this run was paging are kept: the run was
    // served its pages before the mapping existed, so their absence here is
    // not evidence they left the territory.
    const survivingHcpMdmIds = this.prefetchedHcpMdmIds.size
      ? new Set([...writtenHcpMdmIds, ...this.prefetchedHcpMdmIds])
      : writtenHcpMdmIds;

    const deletedCount = await this.removeDocumentsOutsideSnapshot(survivingHcpMdmIds);

    // Stamped from the refreshDate the **rows** carried, not the one /rx/periods
    // reported, so the stamp describes the data actually on disk. The two are
    // proven equal above — a mismatch abandons the ingest — so this is the same
    // value, just sourced from the records it certifies.
    //
    // Stamped only once every page has landed: marking a partial ingest as
    // current would make the next run skip it, leaving part of the territory
    // permanently without data. A document that `bulkDocs` rejected makes this
    // exactly that kind of partial ingest, and holding the watermark alone only
    // rescues a newly-mapped HCP — one already in the territory is only ever
    // re-delivered by a full pull, which is what an unstamped snapshot gets.
    if (this.failedWriteCount) {
      console.error(
        `${LOG} snapshot not stamped — ${this.failedWriteCount} document(s) failed to write, so the next run pulls the territory again`,
      );
      return savedDocumentCount > 0 || deletedCount > 0;
    }

    const storedRefreshDate = refreshDate ?? periodsResponse.refreshDate;
    await this.savePeriodsDocument(periodsResponse, storedRefreshDate);

    return savedDocumentCount > 0 || deletedCount > 0;
  }

  /**
   * Ids only — no documents loaded.
   *
   * Deliberately not cached across calls. The result is only ever used to ask
   * "is anything actually on disk", and the database is emptied by a full
   * sync's purge without this service being told — so a listing held from an
   * earlier ingest would claim rows that are gone, and the caller would skip
   * the re-fetch that was supposed to replace them.
   */
  private async listStoredDocumentIds(): Promise<string[]> {
    const allDocsResponse: any = await this.diskService.batchFetch(
      {
        include_docs: false,
        startkey: DB_KEY_PREFIXES.RX_TREND_SERIES,
        endkey: `${DB_KEY_PREFIXES.RX_TREND_SERIES}￰`,
      },
      false,
    );

    return (allDocsResponse?.rows ?? []).map((row) => row.id).filter(Boolean);
  }

}
